import { isDefined } from '@sgme/fp';
import { type ReactElement, useMemo } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import type { Currencies } from '@/App/tiles/FlexiForward/utils.ts';
import { getFlexiForwardQuote } from '@/store/flexi-forward/helpers/rfs.ts';
import { DEFAULT_CLIENTS_WAY } from '@/store/flexi-forward/state.ts';

export function ClientWayAndRate({
  currencies,
  precision,
  isInternalSales,
}: {
  currencies: Currencies;
  precision: number;
  isInternalSales: boolean;
}): ReactElement {
  const { clientWay, currency1, currency2, notionalCurrencyIndex, forwardRate } = useFlexiForwardSelector(
    (flexiForward) => {
      const currencyPair = flexiForward.properties.currencyPair ?? '';
      const { currency1, currency2 } = currencies;

      const quote = getFlexiForwardQuote(flexiForward.properties.rfs);

      const solvable = flexiForward.properties.solvable;
      const solvingTypeFromUi =
        flexiForward.properties?.solvingType === 'solveUpfront' ? 'PremiumPaymentAmount' : 'ForwardRate';
      const solvingType = isDefined(solvable) ? solvable : solvingTypeFromUi;
      const isSolvedForRate = solvingType === 'ForwardRate';
      const forwardRate =
        isSolvedForRate && isDefined(quote?.forwardRate)
          ? Number.parseFloat(quote?.forwardRate)
          : isDefined(flexiForward.properties.rate)
            ? flexiForward.properties.rate
            : 0;

      return {
        clientWay: flexiForward.properties?.clientsWay ?? DEFAULT_CLIENTS_WAY,
        currencyPair,
        currency1,
        currency2,
        notionalCurrencyIndex: flexiForward.properties.notionalCurrencyIndex ?? 1,
        forwardRate,
      };
    },
  );

  const displayedClientsWay = useMemo(
    () => (notionalCurrencyIndex === 1 ? clientWay : clientWay === 'Buy' ? 'Sell' : 'Buy'),
    [clientWay, notionalCurrencyIndex],
  );

  const displayedCurrency = useMemo(
    () => (notionalCurrencyIndex === 1 ? currency1 : currency2),
    [currency1, currency2, notionalCurrencyIndex],
  );

  return (
    <div className="text-nowrap fs-12 line-height-1 ps-1">
      <FormattedMessage
        id={`flexiForwardTile.rates.streaming.wayAction.${displayedClientsWay}`}
        values={{ isInternalSales, currency: displayedCurrency }}
      />
      &nbsp;
      <span className="fw-semibold">
        <FormattedNumber value={forwardRate} minimumFractionDigits={precision} />
        &nbsp;Rate
      </span>
    </div>
  );
}
